import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminApproveMcDocument,
  AdminFetchMcReviewDocuments,
  AdminRejectMcDocument,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  ApproveMcDocumentPayload,
  RejectMcDocumentPayload,
} from 'src/interfaces/documentReview'
import type { McReviewQueueRow } from 'src/interfaces/mcDocumentReview'
import { sortReviewQueueByWaiting } from 'src/utils/documentReviewList'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

export function* onFetchMcReviewDocuments(action: AsyncAction<undefined>) {
  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.InternalMcDocumentReviewDocuments,
      { method: 'GET' },
    )

    // The endpoint answers `{ data }`; the normalizer also handles a raw array, so this call
    // site stays correct if the wrapper is ever dropped.
    const documents = sortReviewQueueByWaiting(normalizeApiListPayload<McReviewQueueRow>(payload))

    yield put(AdminFetchMcReviewDocuments.success(documents))

    if (action.next) {
      action.next(null, documents)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.mcDocumentReview.onFetchMcReviewDocuments] error',
    })

    yield put(AdminFetchMcReviewDocuments.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load the master contractor queue'))
    }
  }
}

export function* onApproveMcDocument(action: AsyncAction<ApproveMcDocumentPayload>) {
  try {
    yield call(callSecureApi, Endpoints.InternalMcDocumentReviewApproveDocument, {
      method: 'POST',
      body: action.payload,
    })

    yield put(AdminApproveMcDocument.success())

    if (action.next) {
      action.next(null)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.mcDocumentReview.onApproveMcDocument] error' })

    yield put(AdminApproveMcDocument.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to approve the document'))
    }
  }
}

export function* onRejectMcDocument(action: AsyncAction<RejectMcDocumentPayload>) {
  try {
    yield call(callSecureApi, Endpoints.InternalMcDocumentReviewRejectDocument, {
      method: 'POST',
      body: action.payload,
    })

    yield put(AdminRejectMcDocument.success())

    if (action.next) {
      action.next(null)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.mcDocumentReview.onRejectMcDocument] error' })

    yield put(AdminRejectMcDocument.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to reject the document'))
    }
  }
}

export function* mcDocumentReviewSaga() {
  yield takeLatest(AdminFetchMcReviewDocuments.type.REQUEST, onFetchMcReviewDocuments)
  // Mutations use takeEvery, not takeLatest — a doubled click before the button disables must
  // not have its first request silently dropped.
  yield takeEvery(AdminApproveMcDocument.type.REQUEST, onApproveMcDocument)
  yield takeEvery(AdminRejectMcDocument.type.REQUEST, onRejectMcDocument)
}
